import { csv2json } from "json-2-csv"
import React, { useRef, useState } from "react"
import toast from "react-hot-toast"
import { delay, InputLabel, PageContainer, PageWrapper, StandardButtonWithSpinner } from "swiipe.portal.shared"
import { addDigitealInvoicingDetailsThunk, IDigitealCsvRow } from "../../store/thunks/digitealThunks"
import useReduxDispatch from "../../store/useReduxDispatch"
import { getFileAsText, getFilesFromInput } from "../../util/fileUtil"
import "./DigitealInvoicingPage.scss"

interface IDigitealInvoicingPage {}

export interface IFormModel {
    technicalUserUsername: string
    technicalUserPassword: string
}

const DigitealInvoicingPage = ({}: IDigitealInvoicingPage) => {
    const dispatch = useReduxDispatch()
    const importFileInputRef = useRef<HTMLInputElement>(null)
    const [rowCount, setRowCount] = useState<number | undefined>(undefined)
    const [rowCountUploaded, setRowCountUploaded] = useState<number | undefined>(undefined)
    const [rowUploadStart, setRowUploadStart] = useState<number | undefined>(undefined)
    const [rowUploadEnd, setRowUploadEnd] = useState<number | undefined>(undefined)

    return (
        <PageWrapper>
            <PageContainer id="digiteal-invoicing">
                <div className="digiteal-invoicing__import">
                    <div className="digiteal-invoicing__row">
                        <InputLabel title="Import files" className="digiteal-invoicing__import-file-label">
                            <input
                                ref={importFileInputRef}
                                className="digiteal-invoicing__import-file-input"
                                type="file"
                                accept=".json,.csv"
                                multiple
                            ></input>
                        </InputLabel>
                    </div>
                    <StandardButtonWithSpinner
                        title="Import"
                        isSmall
                        containerClass="digiteal-invoicing__action-button"
                        onClick={async () => {
                            await delay(300)
                            const files = getFilesFromInput(importFileInputRef.current)
                            if (files.length === 0) {
                                toast.error("No files selected", { position: "bottom-center" })
                            }

                            const fileContents: IDigitealCsvRow[] = (
                                await Promise.all(
                                    files.map(async (f) => {
                                        const content = (await getFileAsText(f)) as string
                                        return csv2json(content, {}) as IDigitealCsvRow[]
                                    })
                                )
                            ).flat()

                            setRowCount(fileContents.length)
                            setRowCountUploaded(0)

                            let fromRow = 0
                            const step = 1000
                            while (fromRow < fileContents.length) {
                                const toRow = Math.min(fromRow + step - 1, fileContents.length - 1)
                                setRowUploadStart(fromRow)
                                setRowUploadEnd(toRow)
                                await dispatch(addDigitealInvoicingDetailsThunk(fileContents.slice(fromRow, toRow + 1)))
                                fromRow = toRow + 1
                                setRowCountUploaded(toRow + 1)
                            }
                            setRowUploadStart(undefined)
                            setRowUploadEnd(undefined)
                        }}
                    />
                </div>
                <div className="digiteal-invoicing__progress">
                    {rowCount !== undefined && (
                        <p>
                            {rowCountUploaded}/{rowCount} rows uploaded
                        </p>
                    )}
                    {rowUploadStart !== undefined && rowUploadEnd !== undefined && (
                        <p>
                            Uploading rows {rowUploadStart + 1}-{rowUploadEnd + 1}
                        </p>
                    )}
                </div>
            </PageContainer>
        </PageWrapper>
    )
}

export default DigitealInvoicingPage
